import { useState } from 'react';
import { Extension, Mark, Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { NodeViewProps } from '@tiptap/react';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { t } from '../i18n';
import { ActionPopover } from './ActionPopover';
import { Database } from './Database';
import { EmojiPicker } from './EmojiPicker';
import { PageIcon } from './PageIcon';

const BLOCK_BACKGROUNDS = ['gray', 'yellow', 'green', 'blue', 'pink', 'red'] as const;

/** CSS variable for a block background swatch. Unknown values are ignored. */
export function blockBackgroundVar(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (!(BLOCK_BACKGROUNDS as readonly string[]).includes(value)) return null;
  return `var(--color-blk-bg-${value})`;
}

// First-line box of a callout. Headings inside a callout have no extra
// padding (see workspace.css), so the icon box is the line itself.
const CALLOUT_ICON = {
  0: { line: 24, pad: 0, glyph: 20 },
  1: { line: 38, pad: 0, glyph: 30 },
  2: { line: 32, pad: 0, glyph: 24 },
  3: { line: 28, pad: 0, glyph: 22 },
  4: { line: 24, pad: 0, glyph: 20 },
} as const;

export function calloutIconMetrics(
  blockType: string,
  level: number | null,
): { line: number; pad: number; glyph: number } {
  if (level === 1 || level === 2 || level === 3 || level === 4) {
    if (blockType === 'heading') return CALLOUT_ICON[level];
  }
  if (blockType === 'bulletList' || blockType === 'orderedList' || blockType === 'taskList') {
    return { line: 24, pad: 4, glyph: 20 };
  }
  return CALLOUT_ICON[0];
}

function Chevron({ size = 20 }: { size?: number }): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path
        d="m9 6 6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Caret box per heading level, from the prototype's caretBtnStyle:
// [button height, margin-top] and the caret glyph size inside it.
const CARET_BOX: Record<number, { height: number; marginTop: number; size: number }> = {
  0: { height: 24, marginTop: 0, size: 20 },
  1: { height: 38, marginTop: 14, size: 24 },
  2: { height: 32, marginTop: 10, size: 22 },
  3: { height: 28, marginTop: 6, size: 20 },
  4: { height: 24, marginTop: 4, size: 18 },
};

function ToggleView({ node, updateAttributes, editor, getPos }: NodeViewProps): React.ReactElement {
  const open = node.attrs['open'] !== false;
  const level = typeof node.attrs['level'] === 'number' ? node.attrs['level'] : 0;
  const caret = CARET_BOX[level] ?? { height: 24, marginTop: 0, size: 20 };
  const hasBodyChildren = node.childCount > 1;

  function addLine(): void {
    const pos = getPos();
    if (typeof pos !== 'number') return;
    if (!open) updateAttributes({ open: true });
    const end = pos + node.nodeSize - 1;
    editor
      .chain()
      .focus()
      .insertContentAt(end, { type: 'paragraph' })
      .setTextSelection(end + 1)
      .run();
  }

  return (
    <NodeViewWrapper
      className="toggle"
      data-open={open ? 'true' : 'false'}
      data-level={String(level)}
    >
      <button
        type="button"
        className="toggle__chevron"
        contentEditable={false}
        aria-expanded={open}
        aria-label={open ? t('editor.collapse') : t('editor.expand')}
        style={{ height: caret.height, marginTop: caret.marginTop }}
        onClick={() => updateAttributes({ open: !open })}
      >
        <Chevron size={caret.size} />
      </button>
      <div className="toggle__body">
        <NodeViewContent />
        {open && !hasBodyChildren ? (
          <button
            type="button"
            className="toggle__add nb"
            contentEditable={false}
            onClick={addLine}
          >
            {t('editor.addLine')}
          </button>
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}

function CalloutView({ node, updateAttributes }: NodeViewProps): React.ReactElement {
  const icon = typeof node.attrs['icon'] === 'string' ? node.attrs['icon'] : '💡';
  const [picking, setPicking] = useState(false);
  const first = node.firstChild;
  const rawLevel = first?.attrs?.['level'];
  const level = typeof rawLevel === 'number' ? rawLevel : null;
  const box = calloutIconMetrics(first?.type.name ?? 'paragraph', level);
  const background = blockBackgroundVar(node.attrs['bg']);

  return (
    <NodeViewWrapper
      className="callout"
      data-callout=""
      style={background ? { background } : undefined}
    >
      <span contentEditable={false}>
        <ActionPopover
          open={picking}
          onOpenChange={setPicking}
          label={t('page.icon')}
          className="picker picker--icon"
          rootClassName="popover-root"
          trigger={
            <button
              type="button"
              className="callout__icon-button"
              aria-label={t('page.icon')}
              style={{
                width: box.line + 4,
                height: box.line,
                marginTop: box.pad,
                fontSize: box.glyph,
                lineHeight: `${box.line}px`,
              }}
            >
              {icon}
            </button>
          }
        >
          <EmojiPicker
            onPick={(emoji) => {
              updateAttributes({ icon: emoji });
              setPicking(false);
            }}
          />
        </ActionPopover>
      </span>
      <NodeViewContent />
    </NodeViewWrapper>
  );
}

export const Toggle = Node.create({
  name: 'toggle',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      open: {
        default: true,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-open') !== 'false',
        renderHTML: (attributes: { open?: boolean }) => ({
          'data-open': attributes.open === false ? 'false' : 'true',
        }),
      },
      // 0 = plain toggle, 1-4 = toggle heading of that level.
      level: {
        default: 0,
        parseHTML: (element: HTMLElement) => Number(element.getAttribute('data-level') ?? 0),
        renderHTML: (attributes: { level?: number }) => ({
          'data-level': String(attributes.level ?? 0),
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-toggle]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-toggle': '' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ToggleView);
  },
});

/**
 * Anchor for a comment thread. The thread id rides in the document so the
 * highlight survives saves and reloads; the thread itself lives in the API.
 */
export const CommentMark = Mark.create({
  name: 'comment',

  addAttributes() {
    return {
      threadId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-comment-id'),
        renderHTML: (attributes: { threadId?: string | null }) =>
          attributes.threadId ? { 'data-comment-id': attributes.threadId } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'cmt' }), 0];
  },
});

/**
 * A person named in the text. The user id rides in the document, so the
 * notification knows who was named without guessing from the text.
 */
export const Mention = Node.create({
  name: 'mention',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      userId: { default: null },
      name: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-mention-user]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const userId = node.attrs.userId as string | null;
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-mention-user': userId,
        class: 'doc-mention',
      }),
      `@${node.attrs.name as string}`,
    ];
  },
});

/**
 * A link to another page in the workspace. The page id rides in the mark, so
 * the system knows which page is meant and the backlinks of that page can be
 * built from it.
 */
export const PageLink = Mark.create({
  name: 'pageLink',

  addAttributes() {
    return {
      pageId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-page-id'),
        renderHTML: (attributes: { pageId?: string | null }) =>
          attributes.pageId ? { 'data-page-id': attributes.pageId } : {},
      },
      href: {
        default: '',
        parseHTML: (element: HTMLElement) => element.getAttribute('href'),
        renderHTML: (attributes: { href?: string }) =>
          attributes.href ? { href: attributes.href } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'a[data-page-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['a', mergeAttributes(HTMLAttributes, { class: 'page-link' }), 0];
  },
});

/**
 * A reference to a child page: a block that opens the page below this one.
 * The title is a snapshot; the page itself stays the truth.
 */
export const ChildPage = Node.create({
  name: 'childPage',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      pageId: { default: null },
      title: { default: '' },
      icon: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'a[data-child-page]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['a', mergeAttributes(HTMLAttributes, { 'data-child-page': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChildPageView);
  },
});

function ChildPageView({ node, editor }: NodeViewProps): React.ReactElement {
  const pageId = typeof node.attrs['pageId'] === 'string' ? node.attrs['pageId'] : '';
  const title = typeof node.attrs['title'] === 'string' ? node.attrs['title'] : '';
  const icon = typeof node.attrs['icon'] === 'string' ? node.attrs['icon'] : '';

  return (
    <NodeViewWrapper className="childpage" data-child-page="">
      <button
        type="button"
        contentEditable={false}
        className="childpage__link"
        onClick={() => {
          if (!pageId) return;
          editor.commands.focus();
          window.location.hash = `#p/${pageId}`;
        }}
      >
        <span className="childpage__icon" aria-hidden="true">
          {icon ? <PageIcon icon={icon} /> : '📄'}
        </span>
        <span className="childpage__title">{title || t('editor.childPage.unnamed')}</span>
      </button>
    </NodeViewWrapper>
  );
}

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      icon: { default: '💡' },
    };
  },

  parseHTML() {
    return [{ tag: 'aside[data-callout]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['aside', mergeAttributes(HTMLAttributes, { 'data-callout': '' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CalloutView);
  },
});

/**
 * What the database block needs from the page around it. A ref, not a value:
 * the node view must always see the current page without the editor rebuilding.
 */
export interface DatabaseContext {
  pageId: string;
  workspaceId: string;
  openRow: (rowId: string) => void;
}

function DatabaseView({ node, extension }: NodeViewProps): React.ReactElement {
  const context = extension.options.context.current;
  const dataSourceId =
    typeof node.attrs['dataSourceId'] === 'string' ? node.attrs['dataSourceId'] : null;
  return (
    <NodeViewWrapper className="dbblock" contentEditable={false}>
      {context && dataSourceId ? (
        <Database
          dataSourceId={dataSourceId}
          initialViewId={typeof node.attrs['viewId'] === 'string' ? node.attrs['viewId'] : null}
          workspaceId={context.workspaceId}
          onOpenRow={context.openRow}
        />
      ) : (
        <p className="dbblock__missing">{t('database.loading')}</p>
      )}
    </NodeViewWrapper>
  );
}

/**
 * A database lives outside the document: the block only carries the id, the
 * schema and the rows are fetched and saved through the API.
 */
export const DatabaseBlock = Node.create({
  name: 'database',
  group: 'block',
  atom: true,
  selectable: true,

  addOptions() {
    return { context: { current: null } as React.RefObject<DatabaseContext | null> };
  },

  addAttributes() {
    return {
      viewId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-view-id'),
        renderHTML: (attributes: { viewId?: string | null }) =>
          attributes.viewId ? { 'data-view-id': attributes.viewId } : {},
      },
      dataSourceId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-data-source-id'),
        renderHTML: (attributes: { dataSourceId?: string | null }) =>
          attributes.dataSourceId ? { 'data-data-source-id': attributes.dataSourceId } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-database]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-database': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DatabaseView);
  },
});

const ROW_BLOCKS = [
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'taskList',
  'blockquote',
  'codeBlock',
  'horizontalRule',
  'tableOfContents',
  'toggle',
  'callout',
];

const COLUMN_GAP_PX = 52;
export const blockSelectionPluginKey = new PluginKey<number[]>('blockSelection');

/**
 * Prototype `row` + `cw` attributes on top-level blocks (`e4.js` / `e2.js`).
 * Blocks with a matching non-null `row` sit side-by-side in the flex-wrap
 * `.page-editor__body` with a 52px column gap and a proportional width `cw`.
 * A ProseMirror decoration plugin stamps `data-blk-pos`, `.blk--selected`, and
 * column inline styles directly onto top-level block DOM nodes so ProseMirror's
 * DOMObserver never reverts them.
 */
export const BlockRowAttributes = Extension.create({
  name: 'blockRowAttributes',

  addGlobalAttributes() {
    return [
      {
        types: ROW_BLOCKS,
        attributes: {
          row: {
            default: null,
            parseHTML: (element: HTMLElement) => element.getAttribute('data-row') ?? null,
            renderHTML: (attributes: { row?: string | null }) =>
              attributes.row ? { 'data-row': attributes.row } : {},
          },
          cw: {
            default: null,
            parseHTML: (element: HTMLElement) => {
              const raw = element.getAttribute('data-cw');
              return raw !== null ? Number(raw) : null;
            },
            renderHTML: (attributes: { cw?: number | null }) =>
              typeof attributes.cw === 'number' ? { 'data-cw': String(attributes.cw) } : {},
          },
          color: {
            default: null,
            parseHTML: (element: HTMLElement) => element.getAttribute('data-color') ?? null,
            renderHTML: (attributes: { color?: string | null }) =>
              attributes.color ? { 'data-color': attributes.color } : {},
          },
          bg: {
            default: null,
            parseHTML: (element: HTMLElement) => element.getAttribute('data-bg') ?? null,
            renderHTML: (attributes: { bg?: string | null }) =>
              attributes.bg ? { 'data-bg': attributes.bg } : {},
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<number[]>({
        key: blockSelectionPluginKey,
        state: {
          init: () => [],
          apply(tr, prev) {
            const meta = tr.getMeta(blockSelectionPluginKey) as number[] | undefined;
            if (Array.isArray(meta)) return meta;
            return prev;
          },
        },
        props: {
          decorations(state) {
            const selected = new Set(blockSelectionPluginKey.getState(state) ?? []);
            const rowCounts: Record<string, number> = {};
            const rowSums: Record<string, number> = {};
            state.doc.forEach((node) => {
              const row = typeof node.attrs['row'] === 'string' ? node.attrs['row'] : null;
              const cw = typeof node.attrs['cw'] === 'number' ? node.attrs['cw'] : 1;
              if (row) {
                rowCounts[row] = (rowCounts[row] ?? 0) + 1;
                rowSums[row] = (rowSums[row] ?? 0) + cw;
              }
            });

            const rowIndex: Record<string, number> = {};
            const decos: Decoration[] = [];
            state.doc.forEach((node, offset) => {
              const row = typeof node.attrs['row'] === 'string' ? node.attrs['row'] : null;
              const cw = typeof node.attrs['cw'] === 'number' ? node.attrs['cw'] : 1;
              const groupCount = row ? (rowCounts[row] ?? 1) : 1;
              const groupSum = row ? (rowSums[row] ?? 1) : 1;
              const indexInRow = row ? (rowIndex[row] ?? 0) : 0;
              if (row) rowIndex[row] = indexInRow + 1;

              const attrs: Record<string, string> = {
                'data-blk-pos': String(offset),
              };
              if (row && groupCount > 1) {
                attrs['data-row'] = row;
                attrs['data-cw'] = String(cw);
              }
              if (selected.has(offset)) {
                attrs['class'] = 'blk--selected';
              }
              const styles: string[] = [];
              if (groupCount > 1) {
                const ratio = (cw / groupSum).toFixed(5);
                styles.push(
                  `width: calc((100% - ${(groupCount - 1) * COLUMN_GAP_PX}px) * ${ratio})`,
                  'flex: none',
                  'min-width: 0',
                );
                if (indexInRow > 0) {
                  styles.push(`margin-left: ${COLUMN_GAP_PX}px`);
                }
              }
              const blkColor =
                typeof node.attrs['color'] === 'string' && node.attrs['color']
                  ? node.attrs['color']
                  : null;
              const background = blockBackgroundVar(node.attrs['bg']);
              if (blkColor) {
                styles.push(`color: var(--color-blk-text-${blkColor})`);
              }
              // A callout paints its own card. The node-view root is only the
              // block wrapper, so a background there colours the wrong box.
              if (background && node.type.name !== 'callout') {
                styles.push(`background: ${background}`, 'border-radius: 4px');
              }
              if (styles.length > 0) {
                attrs['style'] = styles.join('; ');
              }
              decos.push(Decoration.node(offset, offset + node.nodeSize, attrs));
            });

            return DecorationSet.create(state.doc, decos);
          },
        },
      }),
    ];
  },
});
